import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ExternalLink, PlayCircle } from 'lucide-react';
import { projects } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, PageHero, Ridgeline, fadeInUp } from '../components/layout/Section';
import { SpotlightCard, StatusPill, Segmented, Reveal } from '../components/layout/Tech';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const ProjectMark = ({ project }) => {
  const Icon = project.icon;
  if (project.logo) {
    return (
      <span className="brand-mark" style={{ width: 56, height: 56, background: project.logoBg || '#fff' }}>
        <img src={project.logo} alt={`${project.title} logo`} />
      </span>
    );
  }
  return <span className="icon-well" style={{ width: 56, height: 56 }}><Icon className="w-6 h-6" /></span>;
};

const filterOptions = [
  { value: 'all', label: 'All Projects' },
  { value: 'ai', label: 'AI as a Service' },
  { value: 'saas', label: 'SaaS Platforms' },
  { value: 'client', label: 'Client Work' }
];

const ProjectCard = ({ project, index, onViewProduct }) => (
  <Reveal delay={Math.min(index, 3) * 0.07} className="h-full">
    <SpotlightCard
      id={project.id}
      accent={project.accentClass}
      className="h-full scroll-mt-28"
      style={{ '--card-pad': '0' }}
    >
      <div className="win-bar" style={{ borderBottomColor: 'var(--card-border)' }}>
        <span className="win-dots"><i /><i /><i /></span>
        <span className="win-url">{project.website ? project.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : project.category.toLowerCase().replace(/\s+/g, '-')}</span>
        <span className="win-right"><StatusPill status={project.status} /></span>
      </div>

      <div className="flex flex-col gap-5" style={{ padding: '2.25rem' }}>
        <div className="flex items-center gap-4">
          <ProjectMark project={project} />
          <div className="flex flex-col gap-1.5">
            <span className="chip" style={{ color: 'var(--card-accent)', borderColor: 'var(--card-accent)' }}>{project.category}</span>
            {project.tagline && <span className="kicker" style={{ letterSpacing: '0.06em', textTransform: 'none' }}>“{project.tagline}”</span>}
          </div>
        </div>

        <div>
          <h3 className="mb-3" style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
            {project.title}
          </h3>
          <p className="mb-4" style={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'var(--text-secondary)' }}>
            {project.description}
          </p>
          <p className="mb-1" style={{ fontSize: '0.85rem', lineHeight: 1.55, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            {project.partnership}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {project.features.map((f) => (
            <span key={f} className="feature-line"><CheckCircle2 className="w-3.5 h-3.5" /> {f}</span>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          {project.demoVideo ? (
            <button type="button" onClick={() => onViewProduct(project)} className="btn-primary btn-accent website-cta self-start">
              View Product Video <PlayCircle className="w-3.5 h-3.5" />
            </button>
          ) : null}
          {project.website ? (
            <a href={project.website} target="_blank" rel="noreferrer" className="btn-secondary website-cta self-start">
              Visit Website <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : null}
        </div>
      </div>
    </SpotlightCard>
  </Reveal>
);

export default function Projects() {
  const [filter, setFilter] = useState('all');
  const [activeDemo, setActiveDemo] = useState(null);
  const filtered = filter === 'all' ? projects : projects.filter(p => p.category.toLowerCase().includes(filter));

  return (
    <main>
      <PageHero
        tone="teal"
        eyebrow="Our Projects"
        title="Building Intelligent Products"
        subtitle="In partnership with industry experts, we're building AI-powered products across healthcare, education, commerce, and enterprise."
        glows={[
          { color: 'rgba(52,211,153,0.26)', top: '-10%', left: '-6%', size: 560 },
          { color: 'rgba(34,211,238,0.22)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        ridge
      >
        <div className="flex flex-wrap justify-center gap-2 mt-8">
          <StatusPill status="Live" /><StatusPill status="Demo" /><StatusPill status="In Development" />
        </div>
      </PageHero>

      <Section tone="light" pattern="dots" glows={[{ color: 'rgba(167,139,250,0.3)', top: '-10%', right: '-8%', size: 560 }]} contentWidth={1160}>
        <motion.div {...fadeInUp} className="flex flex-wrap items-center justify-center gap-3 mb-14">
          <Segmented options={filterOptions} value={filter} onChange={setFilter} />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} onViewProduct={setActiveDemo} />
          ))}
        </div>
      </Section>

      <Dialog open={Boolean(activeDemo)} onOpenChange={(open) => !open && setActiveDemo(null)}>
        <DialogContent className="project-demo-dialog">
          <DialogHeader>
            <DialogTitle>{activeDemo?.title}</DialogTitle>
            <DialogDescription>Product walkthrough from Globistaan.</DialogDescription>
          </DialogHeader>
          {activeDemo?.demoVideo && (
            <div className="project-demo-frame">
              <iframe
                src={activeDemo.demoVideo}
                title={`${activeDemo.title} product demo`}
                className="absolute inset-0 w-full h-full border-0"
                allow="autoplay; fullscreen; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Section
        tone="plum"
        rule={false}
        glows={[
          { color: 'rgba(167,139,250,0.3)', top: '-30%', left: '15%', size: 640 },
          { color: 'rgba(251,113,133,0.2)', bottom: '-30%', right: '10%', size: 560 }
        ]}
        contentWidth={700}
        padding="py-28"
      >
        <ParticlesBg />
        <motion.div {...fadeInUp} className="text-center relative z-10">
          <h2 className="h-section mb-5" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)' }}>Interested in Our Projects?</h2>
          <p className="lede mx-auto mb-10">Explore partnership, investment, or early access opportunities.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/investors" className="btn-primary btn-accent" style={{ padding: '1rem 2.4rem', fontSize: '0.85rem' }}>
              Investment Opportunities <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary" style={{ padding: '1rem 2.4rem', fontSize: '0.85rem' }}>Get in Touch</Link>
          </div>
        </motion.div>
        <Ridgeline opacity={0.7} height={140} />
      </Section>
    </main>
  );
}
